/**
 * Impersonation Banner Component
 *
 * CRITICAL safety feature that displays during admin impersonation sessions.
 *
 * DESIGN:
 * - Full-width sticky banner at top of viewport
 * - Red background (bg-red-600) with white text
 * - Shows: Organization name, time remaining, End Session button
 * - Countdown updates every second
 *
 * BUG FIXES:
 * - Bug #7: Reads from localStorage as backup (survives Context refresh)
 * - Bug #15: No negative time values shown
 *
 * INTEGRATION:
 * - Add to TailwindApp.js (always visible when impersonating)
 * - Uses useImpersonation hook from impersonationProvider
 */

import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useImpersonation } from '../../platform/auth/impersonationProvider';

// Storage keys
const SESSION_KEY = 'sr.impersonation.session';
const TARGET_ORG_KEY = 'sr.impersonation.targetOrgId';

export function ImpersonationBanner() {
  const { session, endImpersonation, isImpersonating, timeRemaining } = useImpersonation();
  const [localSession, setLocalSession] = useState(null);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [isEnding, setIsEnding] = useState(false);

  // Bug #7 FIX: Read from localStorage as backup (check every second)
  useEffect(() => {
    const checkSession = () => {
      // Try Context first
      if (session) {
        setLocalSession(session);
        return;
      }

      // Fallback to localStorage
      try {
        const storedSession = localStorage.getItem(SESSION_KEY);
        const targetOrgId = localStorage.getItem(TARGET_ORG_KEY);

        if (storedSession && targetOrgId) {
          const parsed = JSON.parse(storedSession);
          setLocalSession(parsed);
        } else {
          setLocalSession(null);
        }
      } catch (error) {
        console.error('[BANNER] Failed to read session from localStorage:', error);
        setLocalSession(null);
      }
    };

    // Check immediately
    checkSession();

    // Check every second (not just every minute)
    const interval = setInterval(checkSession, 1000);
    return () => clearInterval(interval);
  }, [session]);

  // Update seconds remaining every second
  useEffect(() => {
    if (!localSession) {
      setSecondsRemaining(0);
      return;
    }

    const updateTimer = () => {
      const now = new Date();
      const expiry = new Date(localSession.expiresAt);
      const remaining = expiry.getTime() - now.getTime();

      // Bug #15 FIX: No negative values
      setSecondsRemaining(Math.max(0, Math.floor(remaining / 1000)));
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [localSession]);

  // Format time remaining
  const formatTimeRemaining = (seconds: number): string => {
    if (seconds <= 0) return 'Expired';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Handle end session
  const handleEndSession = async () => {
    setIsEnding(true);
    try {
      await endImpersonation();
    } catch (error) {
      console.error('[BANNER] Failed to end session:', error);
      setIsEnding(false);
    }
  };

  // Don't show banner if not impersonating
  if (!localSession && !isImpersonating) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white shadow-lg">
      <div className="max-w-full px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Warning Icon & Message */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm sm:text-base">
                Impersonating:
              </span>
              <span className="font-bold text-sm sm:text-base truncate">
                {localSession?.organizationName || 'Unknown Organization'}
              </span>
              <span className="text-red-200 text-xs sm:text-sm">|</span>
              <span className="text-sm whitespace-nowrap">
                Session expires in: <span className="font-semibold">{formatTimeRemaining(secondsRemaining)}</span>
              </span>
            </div>
          </div>

          {/* End Session Button */}
          <button
            onClick={handleEndSession}
            disabled={isEnding}
            className="flex items-center gap-2 px-4 py-1.5 bg-white text-red-600 rounded-md hover:bg-red-50 transition-colors font-medium text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isEnding ? (
              <>
                <div className="h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                Ending...
              </>
            ) : (
              <>
                <X className="h-4 w-4" />
                End Session
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
